import { useQuery } from '@tanstack/react-query';
import { formatClientNumbers } from '@wally-reseller/shared';
import { Skeleton } from '@wally-reseller/ui/badge';
import { Button } from '@wally-reseller/ui/button';
import { ErrorState } from '@wally-reseller/ui/empty-state';
import { ArrowLeft, Download } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { api } from '@/lib/api';
import { useFormatDate, useFormatMoney, useFormatMonth, useT } from '@/lib/i18n';
import { StatementBadge } from './StatementsPage';

/** One published statement with its lines (art. 3.4 evidence) and its PDF. */
export function StatementPage() {
  const { id = '' } = useParams();
  const t = useT();
  const fmt = useFormatDate();
  const money = useFormatMoney();
  const month = useFormatMonth();
  const query = useQuery({ queryKey: ['statements', id], queryFn: () => api.statement(id) });

  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-40 w-full" />;
  const s = query.data;
  return (
    <div className="max-w-4xl space-y-5">
      <Link
        to="/decomptes"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ArrowLeft className="size-3.5" />
        {t('portal.statement.back')}
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t('portal.statement.title', { month: month(s.month) })}
          </h1>
          <div className="text-sm text-muted-foreground">{s.number}</div>
          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            <div>{t('portal.statement.published', { date: fmt(s.publishedAt) })}</div>
            {s.invoice && (
              <div>
                {t('portal.statement.invoiced', {
                  number: s.invoice.number,
                  date: fmt(s.invoice.date),
                })}
              </div>
            )}
            {s.paidOn ? (
              <div className="font-medium text-success">
                {t('portal.statement.paid', { date: fmt(s.paidOn) })}
              </div>
            ) : (
              <div>{t('portal.statement.due', { date: fmt(s.dueOn) })}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatementBadge status={s.status} />
          <Button asChild size="sm" variant="outline">
            <a href={`/api/portal/statements/${s.id}/pdf`} download>
              <Download />
              {t('portal.statement.pdf')}
            </a>
          </Button>
        </div>
      </div>

      {s.status === 'published' && (
        <div className="rounded-md border border-note-border bg-note p-3 text-sm">
          {t('portal.statement.invoiceHint', {
            amount: money(s.totals.commission),
            number: s.number,
          })}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-background">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">{t('portal.statement.col.lead')}</th>
              <th className="px-4 py-2 font-medium">{t('portal.statement.col.date')}</th>
              <th className="px-4 py-2 font-medium max-sm:hidden">
                {t('portal.statement.col.kind')}
              </th>
              <th className="px-4 py-2 font-medium max-md:hidden">
                {t('portal.statement.col.invoice')}
              </th>
              <th className="px-4 py-2 text-right font-medium max-sm:hidden">
                {t('portal.statement.col.base')}
              </th>
              <th className="px-4 py-2 text-right font-medium">
                {t('portal.statement.col.commission')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {s.lines.map((l) => (
              <tr key={l.lineId} className="align-top">
                <td className="px-4 py-2">
                  {l.registrationId ? (
                    <Link to={`/leads/${l.registrationId}`} className="hover:underline">
                      {l.legalName}
                    </Link>
                  ) : (
                    t('portal.statement.partnerWide')
                  )}
                  {formatClientNumbers(l) && (
                    <div className="text-xs text-muted-foreground">{formatClientNumbers(l)}</div>
                  )}
                  {l.note && <div className="text-xs text-muted-foreground">{l.note}</div>}
                </td>
                <td className="whitespace-nowrap px-4 py-2">{fmt(l.date)}</td>
                <td className="px-4 py-2 max-sm:hidden">{t(`portal.lead.kind.${l.kind}`)}</td>
                <td className="px-4 py-2 text-muted-foreground max-md:hidden">
                  {l.invoiceNumber ?? '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums max-sm:hidden">
                  {l.kind === 'correction' ? '—' : money(l.base)}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                  {money(l.commission)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border font-medium">
            <tr>
              <td className="px-4 py-2" colSpan={2}>
                {t('portal.statement.total')}
              </td>
              <td className="max-sm:hidden" />
              <td className="max-md:hidden" />
              <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums max-sm:hidden">
                {money(s.totals.base)}
              </td>
              <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                {money(s.totals.commission)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
